import { Suspense, lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import { ApolloProvider } from '@apollo/client'
import Auth from '@aws-amplify/auth'
import awsAuthConfig from './config/aws-auth'
import { apolloClient } from './lib/apollo'
import theme from './theme/theme'
import NavBar from './components/NavBar'
import { LayoutWrapper } from './components/Layout'
import Login from './pages/Login'
import { AuthProvider } from './context/auth'
import { AlertProvider } from './context/alert'
import { AlertCommon } from './components/Alerts'
import ProtectedRoute from './components/ProtectedRoute'
// import { isDev } from './config/config'

const Dashboard = lazy(() => import('./pages/Dashboard'))
const Glossary = lazy(() => import('./pages/Glossary'))
const GlossaryEdit = lazy(() => import('./components/Glossary/GlossaryEdit'))
const GlossaryNew = lazy(() => import('./components/Glossary/GlossaryNew'))
const Academy = lazy(() => import('./pages/Academy'))
const AcademyEdit = lazy(() => import('./components/Academy/AcademyEdit'))
const AcademyNew = lazy(() => import('./components/Academy/AcademyNew'))
const Certification = lazy(() => import('./pages/Certification'))
const CertificationDetails = lazy(() => import('./components/Certification/CertificationDetails'))
const CertificationEdit = lazy(() => import('./components/Certification/CertificationEdit'))
const CertificationNew = lazy(() => import('./components/Certification/CertificationNew'))
const PracticeSet = lazy(() => import('./pages/PracticeSet'))
const PracticeSetSearch = lazy(() => import('./pages/PracticeSetSearch'))
const ItemBankItem = lazy(() => import('./pages/ItemBank/Item'))
const Newsletter = lazy(() => import('./pages/Newsletter'))
const NewsletterNew = lazy(() => import('./components/Newsletter/NewsletterNew'))
const NewsletterEdit = lazy(() => import('./components/Newsletter/NewsletterEdit'))
const Interview = lazy(() => import('./pages/Interview'))
const InterviewNew = lazy(() => import('./components/Interview/InterviewNew'))
const InterviewEdit = lazy(() => import('./components/Interview/InterviewEdit'))
const Podcast = lazy(() => import('./pages/Podcast'))
const PodcastNew = lazy(() => import('./components/Podcast/PodcastNew'))
const PodcastEdit = lazy(() => import('./components/Podcast/PodcastEdit'))
const Story = lazy(() => import('./pages/Story'))
const StoryNew = lazy(() => import('./components/Story/StoryNew'))
const StoryEdit = lazy(() => import('./components/Story/StoryEdit'))
const Metadata = lazy(() => import('./pages/Metadata'))
const MetadataNew = lazy(() => import('./components/Metadata/MetadataNew'))
const MetadataEdit = lazy(() => import('./components/Metadata/MetadataEdit'))

console.debug('REACT_APP_ENV = ', process.env.REACT_APP_ENV)

// Enables debug logging in amplify
// if (isDev) window.LOG_LEVEL = 'DEBUG'
Auth.configure(awsAuthConfig)
window.apollo = apolloClient

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ApolloProvider client={apolloClient}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <AlertProvider>
                <Suspense fallback={<div>Loading...</div>}>
                  <CssBaseline />
                  <NavBar />
                  <AlertCommon />
                  <LayoutWrapper>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route element={<ProtectedRoute />}>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/glossary" element={<Glossary />} />
                        <Route path="/glossary/:termId" element={<GlossaryEdit />} />
                        <Route path="/glossary/new" element={<GlossaryNew />} />
                        <Route path="/academy" element={<Academy />} />
                        <Route path="/academy/:cwid" element={<AcademyEdit />} />
                        <Route path="/academy/new" element={<AcademyNew />} />
                        <Route path="/cert" element={<Certification />} />
                        <Route path="/cert/:cwid" element={<CertificationDetails />} />
                        <Route path="/cert/:cwid/edit" element={<CertificationEdit />} />
                        <Route path="/cert/new" element={<CertificationNew />} />
                        <Route path="/interview" element={<Interview />} />
                        <Route path="/interview/:cwid" element={<InterviewEdit />} />
                        <Route path="/interview/new" element={<InterviewNew />} />
                        <Route path="/practiceSet/search" element={<PracticeSetSearch />} />
                        <Route path="/practiceSet/:cwid" element={<PracticeSet />} />
                        <Route path="/itemBank/item/:cwid" element={<ItemBankItem />} />
                        <Route path="/newsletter" element={<Newsletter />} />
                        <Route path="/newsletter/new" element={<NewsletterNew />} />
                        <Route path="/newsletter/:cwid" element={<NewsletterEdit />} />
                        <Route path="/podcast" element={<Podcast />} />
                        <Route path="/podcast/new" element={<PodcastNew />} />
                        <Route path="/podcast/:cwid" element={<PodcastEdit />} />
                        <Route path="/story" element={<Story />} />
                        <Route path="/story/new" element={<StoryNew />} />
                        <Route path="/story/:cwid" element={<StoryEdit />} />
                        <Route path="/metadata" element={<Metadata />} />
                        <Route path="/metadata/new" element={<MetadataNew />} />
                        <Route path="/metadata/:cwid/edit" element={<MetadataEdit />} />
                      </Route>
                    </Routes>
                  </LayoutWrapper>
                </Suspense>
              </AlertProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </ApolloProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
